import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { DonateButton } from "@/components/site/DonateButton";
import { GALLERY_ITEMS, GALLERY_CATEGORIES } from "@/data/gallery";
import { cn } from "@/lib/utils";
import { Seo } from "@/components/site/Seo";

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const reduce = useReducedMotion();
  const lightboxRef = useRef(null);

  const items =
    filter === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((i) => i.category === filter);

  const open = (idx) => setLightboxIndex(idx);
  const close = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i - 1 + items.length) % items.length)),
    [items.length]
  );
  const next = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    // Capture phase on document so the first Escape closes regardless of focus.
    document.addEventListener("keydown", onKey, true);
    document.body.style.overflow = "hidden";
    lightboxRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = "";
    };
  }, [lightboxIndex, close, prev, next]);

  const active = lightboxIndex !== null ? items[lightboxIndex] : null;

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Gallery — RDA Glasgow"
        description="Photos of life at RDA Glasgow — riders, horses, volunteers and our centre in Summerston. Filter by category and take a closer look."
      />
      <PageHero
        title="Gallery — Life at RDA Glasgow"
        subtitle="Smiles, ponies and proud moments from around the yard, the school and the paddock."
        image={GALLERY_ITEMS[1].src}
        imageAlt={GALLERY_ITEMS[1].alt}
      />

      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Filter chips */}
          <div className="flex flex-wrap gap-3" data-testid="gallery-filters" role="tablist" aria-label="Gallery filters">
            {GALLERY_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setFilter(cat);
                  setLightboxIndex(null);
                }}
                role="tab"
                aria-selected={filter === cat}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                className={cn(
                  "rounded-full px-5 py-2 text-sm font-semibold transition-colors min-h-[44px]",
                  filter === cat
                    ? "bg-rda-teal text-white shadow-soft"
                    : "bg-white text-rda-ink border border-border hover:border-rda-teal hover:text-rda-teal"
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Masonry grid */}
          <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" data-testid="gallery-grid">
            {items.map((item, idx) => (
              <motion.button
                key={item.src}
                layout
                initial={reduce ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: reduce ? 0 : (idx % 6) * 0.04 }}
                onClick={() => open(idx)}
                data-testid={`gallery-tile-${idx}`}
                className="group mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl shadow-soft focus:outline-none focus:ring-4 focus:ring-rda-teal/30"
              >
                <div className="relative">
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-rda-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="p-4 text-left text-sm font-medium text-white">{item.caption}</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Get involved CTA band */}
      <section className="bg-rda-teal" data-testid="gallery-cta-band">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Reveal>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-white">
              Want to be part of the picture?
            </h2>
            <p className="mt-4 text-white/85 max-w-xl mx-auto">
              Volunteer your time or support our work — and help create more moments like these.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <DonateButton size="lg" withIcon className="bg-white text-rda-tealDark hover:bg-white/90 px-7 h-12" />
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-white/70 bg-transparent text-white hover:bg-white/10 font-semibold px-7 h-12"
                data-testid="gallery-volunteer-cta"
              >
                <Link to="/volunteering">Volunteer</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-rda-ink/95 p-4"
            data-testid="gallery-lightbox"
            ref={lightboxRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={active.caption}
            onClick={close}
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="lightbox-close"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous image"
              data-testid="lightbox-prev"
              className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next image"
              data-testid="lightbox-next"
              className="absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
            <motion.figure
              key={active.src}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="max-h-[85vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.alt} className="max-h-[75vh] w-auto rounded-xl object-contain" />
              <figcaption className="mt-4 text-center text-white/90" data-testid="lightbox-caption">
                {active.caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
